import { InputAdornment } from "@mui/material";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { findConditionLabel } from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/utils";
import { useMessage } from "hooks/intl";
import { get } from "lodash";
import { MemberRegistrationDate as MemberRegistrationDateType } from "types/segments";
import { formatNumberValue } from "utilities/numbers";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";
import { segmentConditions } from "./helpers";

export const initialValuesMemberRegistrationDate: MemberRegistrationDateType = {
  operator: { code: "" },
  days: 0,
  type: "member_registration_date",
};

export function MemberRegistrationDate({
  formField,
  values,
  status,
  readOnly,
  setFieldValue,
}: {
  formField: string;
  values: any;
  status: any;
  readOnly?: boolean;
  setFieldValue: (field: string, value: any) => void;
}) {
  const classes = useStyles();
  const value = get(values, formField);

  const { message } = useMessage();

  const getInputAdornmentValue = (value: number) =>
    value === 1 ? "dayAgo" : "daysAgo";

  const isDaysRange = value.operator.code === "is_number_between";
  const daysError = get(status, "days");
  const fromDaysError = get(status, "fromDays");
  const toDaysError = get(status, "toDays");

  return (
    <>
      {value.isConditionSaved || readOnly ? (
        <>
          {message({
            id: findConditionLabel(value.operator.code),
          }).toLocaleLowerCase()}
          {isDaysRange ? (
            <>
              {message({ id: "is_between" }).toLocaleLowerCase()}{" "}
              <BoxElement
                content={formatNumberValue(value?.fromDays, "")}
                error={fromDaysError}
              />{" "}
              {message({ id: "and" }).toLocaleLowerCase()}{" "}
              <BoxElement
                content={formatNumberValue(value?.toDays, "")}
                error={toDaysError}
              />
            </>
          ) : (
            <BoxElement
              content={formatNumberValue(value?.days, "")}
              error={daysError}
            />
          )}
          {message({
            id: getInputAdornmentValue(value?.toDays || value?.days),
          }).toLowerCase()}
        </>
      ) : (
        <>
          <SelectInput
            data-test-id="operator"
            items={segmentConditions.map(
              ({ id, label }: { id: string; label: string }) => ({
                id: id,
                label: message({ id: label }),
              }),
            )}
            label={<RequiredFieldLabel text={message({ id: "condition" })} />}
            name={`${formField}.operator.code`}
            onChange={(_, value) => {
              if (value === "is_number_between") {
                setFieldValue(`${formField}.days`, undefined);
                setFieldValue(`${formField}.fromDays`, 0);
                setFieldValue(`${formField}.toDays`, 0);
              } else {
                setFieldValue(`${formField}.days`, 0);
                setFieldValue(`${formField}.fromDays`, undefined);
                setFieldValue(`${formField}.toDays`, undefined);
              }
            }}
          />
          {isDaysRange && (
            <>
              <NumberFormatInput
                allowNegative={false}
                className={classes.input}
                data-test-id="days-from"
                label={
                  <RequiredFieldLabel text={message({ id: "daysFrom" })} />
                }
                name={`${formField}.fromDays`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({
                        id: getInputAdornmentValue(value.fromDays),
                      }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
              />
              <NumberFormatInput
                allowNegative={false}
                className={classes.input}
                data-test-id="days-to"
                label={<RequiredFieldLabel text={message({ id: "daysTo" })} />}
                name={`${formField}.toDays`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({
                        id: getInputAdornmentValue(value.toDays),
                      }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
          {!isDaysRange && !!value.operator.code && (
            <NumberFormatInput
              allowNegative={false}
              data-test-id="days"
              label={<RequiredFieldLabel text={message({ id: "days" })} />}
              name={`${formField}.days`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {message({
                      id: getInputAdornmentValue(value.days),
                    }).toLowerCase()}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </>
      )}
    </>
  );
}
